import { Grid, Box, Typography, IconButton, Menu, MenuItem, SxProps } from '@mui/material';
import React, { useState, type JSX } from 'react';
import { MoreHoriz, Print } from '@mui/icons-material';
import { Theme } from '@mui/system';

const formatLabel = (label: string) => {
    return label
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

interface InventoryGridRowProps {
    label: string;
    originalLabel: string;
    value: string | number;
    lg?: number;
    xl?: number;
    md?: number;
    sm?: number;
    labelStyles?: SxProps<Theme>;
    valueStyles?: SxProps<Theme>;
    xs?: number;
    fields?: string[];
    // If queryParam is provided, the value will be appended as the query parameter to the baseUrl
    links?: { [key: string]: { baseUrl: string, queryParam?: string } };
    elementStyleActive?: boolean,
    lowercaseFieldName?: string,
    elementStyles?: SxProps,
}

interface IActionItem {
    icon: JSX.Element;
    label: string;
    onClick: (data?: any) => void;
}

interface ICardData {
    label: string;
    value: any
}

/**
 * @deprecated
 */
export interface CardProps {
    /** the object used to render information on the card */
    data: ICardData;
    /** (optional) conditionally renders hot dog menu (default is false) */
    hasAction?: boolean;
    /** (optional) a list of items to be rendered by the card action button */
    actionItems?: Array<IActionItem>;
    lg?: number;
    xl?: number;
    md?: number;
    sm?: number;
    xs?: number;
    /** (optional) applied to the top-level Box component */
    styles?: SxProps;
    /** (optional) applied to all labels */
    labelStyles?: SxProps<Theme>;
    /** (optional) applied to all values */
    valueStyles?: SxProps<Theme>;
    /** (optional) a function component to render at the bottom of the card */
    rowMessageComponent?: () => React.JSX.Element;
    /** (optional) all fields to display on the card. Passed in as camel-case to be formatted by the component.  */
    fields?: string[];
    /** (optional)values to be rendered as links. if queryParam is provided, the value will be appended as the query parameter to the baseUrl */
    links?: { [key: string]: { baseUrl: string, queryParam?: string } };
    /** (optional) conditionally applies elementStyles to the parent Box component of a row */
    elementStyleActive?: boolean,
    /** (optional) conditionally applied to the parent Box component of a row */
    elementStyles?: SxProps,
    /** (optional) a single fieldName that will be formatted to lowercase */
    lowercaseFieldName?: string,
    /** affects the number of columns in a row */
    variant: 'primary' | 'secondary'
}

const defaultActionItems = [
    {
        icon: <Print fontSize="medium" color="primary" />,
        label: 'Print',
        onClick: () => {
            console.log('print');
        },
    },
];

const InventoryGridRow = ({ label, originalLabel, value, labelStyles, valueStyles, fields, links, elementStyleActive, elementStyles, lowercaseFieldName, ...other }: InventoryGridRowProps) => {

    const [isHovered, setIsHovered] = useState(false);

    const handleMouseEnter = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };


    const defaultLabelStyles: SxProps<Theme> = {
        color: 'rgba(0, 0, 0, 0.87)',
        fontSize: '1em',
        fontWeight: 400,
        lineHeight: '1.5em',
        letterSpacing: '0.01em',
        fontFamily: 'Inter',
        textTransform: 'uppercase',
    };

    const defaultValueStyles: SxProps<Theme> = {
        ...defaultLabelStyles,
        color: 'rgba(0, 0, 0, 0.60)',
        textTransform: 'capitalize',
        wordWrap: 'break-word',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        transition: 'text-overflow 0.3s',
    };

    const formatStringLabel = (incoming: string) => {
        const formatted = incoming.toLowerCase();

        return formatted.replace(" ", "")
    };

    if (lowercaseFieldName && formatStringLabel(lowercaseFieldName) === formatStringLabel(label)) {
        defaultValueStyles.textTransform = ""
    }

    if (fields && fields.length > 0 && !fields.includes(originalLabel)) {
        return null;
    }

    const handleClick = (link: string, dynamic: string, queryParam?: string) => {
        let url = link;
        if (queryParam) {
            const queryParams = new URLSearchParams();
            queryParams.append(queryParam, dynamic);
            if (url.endsWith('?') || url.endsWith('&')) {
                url = url + queryParams.toString();
            } else {
                url = `${url}?${queryParams}`;
            }
        } else {
            url = url.endsWith('/') ? url + dynamic : `${url}/${dynamic}`;
        }
        window.open(url, '_blank');
    };

    const valuesToLink = String(value).split(',');

    return (
        <Grid xs={12} sm={6} md={2} item {...other}>
            <Box sx={{
                ...(elementStyleActive && {
                    backgroundColor: '#fafafa',
                    p: 2,
                    borderBottomLeftRadius: '0.4em',
                    borderBottomRightRadius: '0.4em',
                    ...elementStyles
                })
            }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Typography variant='body1' sx={{
                    ...defaultLabelStyles,
                    ...(labelStyles && labelStyles),
                }}>
                    {label}
                </Typography>
                {
                    links && links[originalLabel]?.baseUrl && valuesToLink.length > 0 ? (
                        <Box sx={{ display: 'flex', gap: '0.5em' }}>
                            {
                                valuesToLink.map((valueToLink: string, index: number) => (
                                    <Typography
                                        component={'a'}
                                        key={index}
                                        variant='body1'
                                        sx={{
                                            ...defaultLabelStyles,
                                            ...(labelStyles && labelStyles),
                                            color: '#3F51B5',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => handleClick(links[originalLabel].baseUrl, valueToLink, links[originalLabel].queryParam)}
                                    >
                                        {index === valuesToLink.length - 1 ? valueToLink : `${valueToLink},`}
                                    </Typography>
                                ))
                            }
                        </Box>
                    ) : <Typography sx={{
                        ...defaultValueStyles,
                        ...(valueStyles && valueStyles),
                        textOverflow: isHovered ? 'unset' : 'ellipsis',
                        whiteSpace: isHovered ? 'normal' : 'nowrap',
                        transition: 'text-overflow 0.3s',
                    }} variant='body2'>
                        {value ? value : '-'}
                    </Typography>
                }
            </Box>
        </Grid>
    );
};

/**
 * Old SpaceUsageCard
 * @param data
 * @param hasAction
 * @param actionItems
 * @param styles
 * @param fields
 * @param rowMessageComponent
 * @param links
 * @param elementStyleActive
 * @param elementStyles
 * @param variant
 * @param lowercaseFieldName
 * @param columnStyling
 * @param props
 * @constructor
 * @deprecated
 */
const GridCard = ({
  data,
  hasAction,
  actionItems = defaultActionItems,
  styles,
  fields,
  rowMessageComponent,
  links,
  elementStyleActive,
  elementStyles,
  variant,
  lowercaseFieldName,
  ...props }
: CardProps) => {
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>();
    const entries = Object.entries(data).filter(([label]) => !fields || fields.includes(label));
    const handleOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        if (event.currentTarget instanceof HTMLButtonElement) {
            setMenuAnchorEl(event.currentTarget);
        }
    };

    const handleClose = () => {
        setMenuAnchorEl(null);
    };

    return (
        <Box sx={{
            boxShadow: '0px 0px 0px 1px #E0E0E0',
            p: 1.5,
            borderRadius: '0.4em',
            position: 'relative',
            background: '#f5f5f5',
            ...styles
        }}>
            {
                variant === 'primary' ? <Grid spacing={2} container>
                    {entries.map(([label, value]) => (
                        <InventoryGridRow
                            key={label}
                            label={formatLabel(label)}
                            originalLabel={label}
                            value={value}
                            fields={fields}
                            links={links}
                            elementStyleActive={elementStyleActive}
                            elementStyles={elementStyles}
                            lowercaseFieldName={lowercaseFieldName}
                            {...props}
                        />
                    ))}
                </Grid>
                    : <>
                        <Grid spacing={2} container>
                            {entries.slice(0, 4).map(([label, value]) => (
                                <InventoryGridRow
                                    key={label}
                                    label={formatLabel(label)}
                                    originalLabel={label}
                                    value={value}
                                    fields={fields}
                                    links={links}
                                    elementStyleActive={elementStyleActive}
                                    elementStyles={elementStyles}
                                    md={3}
                                    lowercaseFieldName={lowercaseFieldName}
                                    {...props}
                                />
                            ))}
                        </Grid>
                        <Grid spacing={2} container mt={0.5}>
                            {entries.slice(4, 8).map(([label, value]) => (
                                <InventoryGridRow
                                    key={label}
                                    label={formatLabel(label)}
                                    originalLabel={label}
                                    value={value}
                                    fields={fields}
                                    links={links}
                                    elementStyleActive={elementStyleActive}
                                    elementStyles={elementStyles}
                                    lowercaseFieldName={lowercaseFieldName}
                                    md={3}
                                    {...props}
                                />
                            ))}
                        </Grid>
                        <Grid spacing={2} container mt={0.5}>
                            {entries.slice(8).map(([label, value]) => (
                                <InventoryGridRow
                                    key={label}
                                    label={formatLabel(label)}
                                    originalLabel={label}
                                    value={value}
                                    fields={fields}
                                    links={links}
                                    elementStyleActive={elementStyleActive}
                                    elementStyles={elementStyles}
                                    lowercaseFieldName={lowercaseFieldName}
                                    md={3}
                                    {...props}
                                />
                            ))}
                        </Grid>
                    </>
            }
            {hasAction && <IconButton sx={{
                position: 'absolute',
                top: '38%',
                right: '0.5em',
                color: 'rgba(0, 0, 0, 0.54)'
            }}
                onClick={handleOpen}
            >
                <MoreHoriz />
            </IconButton>}
            {hasAction && Array.isArray(actionItems) && actionItems?.length > 0 && (
                <Menu
                    anchorEl={menuAnchorEl}
                    keepMounted
                    open={Boolean(menuAnchorEl)}
                    onClose={handleClose}
                    sx={{
                        '& .MuiPopover-paper': {
                            boxShadow: '0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }
                    }}
                >
                    {actionItems?.map((item, index) => (
                        <MenuItem key={index} onClick={(e) => { e.stopPropagation(); handleClose(); item.onClick(data) }}>
                            {item.icon}
                            <Typography sx={{ ml: 1 }} variant='body1' color='primary'>
                                {item.label}
                            </Typography>
                        </MenuItem>
                    ))}
                </Menu>
            )}
            {rowMessageComponent && rowMessageComponent()}
        </Box>
    );
};

export default GridCard;
